import countryCodeToFlagEmoji from "country-code-to-flag-emoji";
import { Stripe } from "stripe";
import moment from "moment";
import {
  Badge,
  Box,
  Divider,
  Flex,
  Grid,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconUser } from "@tabler/icons-react";
import { COLORS } from "@/utils/GlobalStyles";

interface InvoiceModalProps {
  opened: boolean;
  onClose: () => void;
  invoiceData: Stripe.Invoice;
  products: Stripe.Product[];
  previewBeforeChange?: boolean;
}

const InvoiceModal = ({
  opened,
  onClose,
  invoiceData,
  products,
  previewBeforeChange = false,
}: InvoiceModalProps) => {
  const formatCurrency = (amount: number) => {
    return `US$${Math.abs(amount).toFixed(2)}`;
  };

  const formatNegativeCurrency = (amount: number) => {
    return amount < 0
      ? `-US$${Math.abs(amount).toFixed(2)}`
      : formatCurrency(amount);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group gap="xs" wrap="wrap">
          <Title order={3}>Upcoming invoice</Title>
          <Badge variant="light" color={COLORS.primary} size="sm">
            Preview
          </Badge>
        </Group>
      }
      styles={{
        content: {
          maxHeight: "90vh",
          overflowY: "auto",
        },
        body: {
          padding: "1rem",
        },
        header: {
          flexWrap: "wrap",
        },
      }}
      className={"modalContainer"}
    >
      <Stack gap="md">
        {/* Header */}
        <Box>
          <Text size="sm" c="dimmed">
            Will be billed to{" "}
            <Text component="span" fw={600} c={COLORS.primary}>
              {invoiceData.customer_name}
            </Text>{" "}
            · {formatCurrency(invoiceData.total / 100)}
          </Text>
        </Box>

        <Grid gutter="md">
          {/* Left Column - Summary */}
          <Grid.Col span={6}>
            <Title order={4} mb="md">
              Summary
            </Title>

            <Stack gap="lg">
              {/* Bill to */}
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Bill to
                </Text>
                <Group gap="xs" mb="xs" wrap="nowrap">
                  <IconUser size={16} color="gray" />
                  <Text size="sm" fw={600} c={COLORS.primary} truncate>
                    {invoiceData.customer_name}
                  </Text>
                </Group>
                <Text
                  size="sm"
                  style={{
                    wordBreak: "break-all",
                  }}
                  fw={600}
                  className={"emailText"}
                >
                  {invoiceData.customer_email}
                </Text>
              </Box>

              {/* Billing details */}
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Billing details
                </Text>
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    {invoiceData.customer_address?.country}
                  </Text>
                  {countryCodeToFlagEmoji(
                    invoiceData.customer_address?.country!,
                  )}
                </Group>
              </Box>
            </Stack>
          </Grid.Col>

          {/* Right Column - Details */}
          <Grid.Col span={6}>
            <Title order={4} mb="md">
              Details
            </Title>

            <Stack gap="lg">
              {/* Will be created */}
              <Box>
                <Text size="sm" fw={500}>
                  Will be created
                </Text>
                <Text size="sm" c={COLORS.primary} fw={600}>
                  {invoiceData.next_payment_attempt
                    ? moment(invoiceData.next_payment_attempt * 1000).format(
                        "MM/DD/YYYY, hh:mm A",
                      )
                    : "Now (Immediate Payment)"}
                </Text>
              </Box>

              {/* Currency */}
              <Box>
                <Text size="sm" fw={500}>
                  Currency
                </Text>
                <Text size="sm" c="dimmed">
                  {invoiceData.currency?.toUpperCase()}
                </Text>
              </Box>

              {/* Connected subscription */}
              <Box>
                <Text size="sm" fw={500}>
                  Connected subscription
                </Text>
                <Group gap="xs" wrap="wrap" className={"subscriptionBadge"}>
                  <Text
                    size="sm"
                    fw={600}
                    c={COLORS.primary}
                    style={{
                      wordBreak: "break-word",
                    }}
                  >
                    {
                      products.find(
                        (product) =>
                          product.id ===
                          //@ts-ignore
                          invoiceData.subscription?.plan?.product,
                      )?.name
                    }
                  </Text>
                  <Badge color={COLORS.primary} size="sm">
                    {previewBeforeChange ? "Will be active" : "Active"}
                  </Badge>
                </Group>
              </Box>

              {/* Billing method */}
              <Box>
                <Text size="sm" fw={500}>
                  Billing method
                </Text>
                <Text
                  size="sm"
                  c="dimmed"
                  style={{
                    wordBreak: "break-word",
                  }}
                >
                  Charge default payment method
                </Text>
              </Box>

              {/* Tax calculation */}
              <Box>
                <Text size="sm" fw={500}>
                  Tax calculation
                </Text>
                <Text size="sm" c="dimmed">
                  {invoiceData.tax
                    ? formatCurrency(invoiceData.tax / 100)
                    : "-"}
                </Text>
              </Box>
            </Stack>
          </Grid.Col>
        </Grid>

        <Divider my="lg" />

        {/* Line Items Table */}
        <Box>
          <ScrollArea
            style={{
              width: "100%",
            }}
            scrollbarSize={8}
          >
            <Table
              highlightOnHover
              withTableBorder={false}
              style={{
                minWidth: "600px",
              }}
              className={"responsiveTable"}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: "200px" }}>Description</Table.Th>
                  <Table.Th ta="center" w={60}>
                    Qty
                  </Table.Th>
                  <Table.Th ta="right" w={100}>
                    Unit price
                  </Table.Th>
                  <Table.Th ta="right" w={100}>
                    Amount
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {invoiceData.lines.data.map((item, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <Text
                        size="sm"
                        style={{
                          wordBreak: "break-word",
                          lineHeight: "1.4",
                        }}
                      >
                        {item.description}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Text size="sm">{item.quantity}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">
                        {item.price?.unit_amount && item.price.unit_amount > 0
                          ? formatCurrency(item.price.unit_amount / 100)
                          : ""}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">
                        {formatNegativeCurrency(item.amount / 100)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>

        <Divider my="lg" />

        {/* Totals */}
        <Box>
          <Stack gap="xs">
            <Flex
              justify="space-between"
              gap="md"
              style={{
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Text size="sm" fw={500}>
                Subtotal
              </Text>
              <Text size="sm" ta="right">
                {formatCurrency(invoiceData.subtotal / 100)}
              </Text>
            </Flex>
            <Flex
              justify="space-between"
              gap="md"
              style={{
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Text size="sm" fw={500}>
                Total excluding tax
              </Text>
              <Text size="sm" ta="right">
                {invoiceData.subtotal_excluding_tax
                  ? formatCurrency(invoiceData.subtotal_excluding_tax / 100)
                  : "-"}
              </Text>
            </Flex>
            <Flex
              justify="space-between"
              gap="md"
              style={{
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Text size="sm" fw={500}>
                Tax
              </Text>
              <Text size="sm" ta="right">
                {invoiceData.tax ? formatCurrency(invoiceData.tax / 100) : "-"}
              </Text>
            </Flex>
            <Divider />
            <Flex
              justify="space-between"
              gap="md"
              style={{
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Text fw={600}>Total</Text>
              <Text fw={600} ta="right">
                {formatCurrency(invoiceData.total / 100)}
              </Text>
            </Flex>
            <Flex
              justify="space-between"
              gap="md"
              style={{
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Text fw={600}>Amount paid</Text>
              <Text fw={600} ta="right">
                {formatCurrency(invoiceData.amount_paid / 100)}
              </Text>
            </Flex>
            <Flex
              justify="space-between"
              gap="md"
              style={{
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Text fw={600}>Amount due</Text>
              <Text fw={600} ta="right">
                {formatCurrency(invoiceData.amount_due / 100)}
              </Text>
            </Flex>
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
};

export default InvoiceModal;
